import { useCallback, useEffect, useState } from 'react';

import useCommonStore from '@/stores/pure/CommonStore';

const usePaginationData = <T extends { [key: string]: any }>(
  fetchCallback: (
    prevData: T[],
    currPage: number,
    targetPage?: number,
  ) => Promise<T[]>,
  flag: string,
  size: number,
  cursorKey?: string,
): [
  T[] | undefined,
  boolean,
  boolean,
  () => void,
  number,
  () => void,
  boolean,
] => {
  const appInit = useCommonStore((state) => state.appInit);
  const [data, setData] = useState<T[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [currPage, setCurrPage] = useState(0);
  const [cursors, setCursors] = useState<PaginationCursorsT>({
    first: null,
    last: null,
  });

  const [hasNextPage, setHasNextPage] = useState(false);

  useEffect(() => {
    setData([]);
  }, [flag]);

  const fetchData = useCallback(
    async (
      prevData: T[],
      cursors?: PaginationCursorsT,
      targetPage?: number,
    ) => {
      try {
        setIsError(false);
        setLoading(true);
        const res = await fetchCallback(prevData, currPage, targetPage);
        if (!res || res.length < size) {
          setHasNextPage(false);
        } else {
          setHasNextPage(true);
        }

        setData((prev) => [...prev, ...res]);
        setCurrPage(targetPage || 0);
        const newCursors: typeof cursors = { first: null, last: null };
        if (res.length > 0) {
          newCursors.first = res[0][cursorKey || 'alias'];
          newCursors.last = res[res.length - 1][cursorKey || 'alias'];
        }
        setCursors(newCursors);
      } catch (e) {
        setIsError(true);
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [cursorKey, fetchCallback],
  );
  const init = useCallback(async () => {
    setCurrPage(0);
    setCursors({ first: null, last: null });
    setData([]);
  }, []);

  useEffect(() => {
    if (appInit) {
      init();
      fetchData([], undefined, 0);
    }
  }, [appInit, fetchData, init]);

  const fetchNextPage = () => {
    fetchData(data, cursors, currPage + 1);
  };

  const refetch = useCallback(() => {
    setData([]);
    setCursors({ first: null, last: null });
    fetchData([], undefined, 0);
  }, [fetchData]);

  return [
    data,
    loading,
    hasNextPage,
    fetchNextPage,
    currPage,
    refetch,
    isError,
  ];
};

export default usePaginationData;
